import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2, AlertCircle, Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@pgs-core/ui/components/card";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@pgs-core/ui/components/select";
import { LoadingSpinner } from "@pgs-core/ui/components/loading-spinner";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { MENU_HINTS, isPageItem, menuItemProblem, menuProblem, moveItem, toSavePayload, type MenuItem } from "../lib/menus";
import type { ClientOutletContext } from "../lib/outlet-context";

// Sentinel Select value for "custom URL" — page options use the slug itself
// (page slugs can't collide with it: they're lowercase-only).
const CUSTOM_URL = "__custom_url__";

interface PageOption {
  slug: string;
  title: string;
  published?: number;
}

interface Menu {
  slug: string;
  name: string;
  items: MenuItem[];
}

function MenuItemRow({ item, index, count, pages, onChange, onMove, onRemove }: {
  item: MenuItem;
  index: number;
  count: number;
  pages: PageOption[];
  onChange: (item: MenuItem) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const problem = menuItemProblem(item);
  // A page item whose page was deleted (or never existed on this older
  // tenant): keep it selectable so the row isn't stuck, but flag it.
  const pageMissing = isPageItem(item) && !pages.some((p) => p.slug === item.page);

  const setLink = (value: string) => {
    if (value === CUSTOM_URL) {
      onChange({ label: item.label, url: item.url ?? "" });
    } else {
      onChange({ label: item.label, page: value });
    }
  };

  return (
    <div className="border-b py-3 last:border-0">
      <div className="flex items-start gap-2">
        <Input
          value={item.label}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...item, label: e.target.value })}
          placeholder="Label"
          aria-label="Item label"
          className="w-44"
        />
        <Select value={isPageItem(item) ? item.page : CUSTOM_URL} onValueChange={setLink}>
          <SelectTrigger className="w-52" aria-label="Item link">
            <SelectValue placeholder="Link to…" />
          </SelectTrigger>
          <SelectContent>
            {pages.map((page) => (
              <SelectItem key={page.slug} value={page.slug}>
                {page.title} {page.published ? "" : "(draft)"}
              </SelectItem>
            ))}
            {pageMissing && item.page && (
              <SelectItem value={item.page}>/{item.page} (page not found)</SelectItem>
            )}
            <SelectItem value={CUSTOM_URL}>Custom URL…</SelectItem>
          </SelectContent>
        </Select>
        {!isPageItem(item) && (
          <Input
            value={item.url ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ label: item.label, url: e.target.value })}
            placeholder="https://…"
            aria-label="Item URL"
            className="flex-1"
          />
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <Button variant="ghost" size="sm" disabled={index === 0} aria-label="Move up" onClick={() => onMove(-1)}>
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" disabled={index === count - 1} aria-label="Move down" onClick={() => onMove(1)}>
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            aria-label="Remove item"
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {problem ? (
        <p className="mt-1.5 text-xs text-destructive">{problem}</p>
      ) : pageMissing ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          This page doesn't exist yet — the link stays hidden on your site until you create and publish it.
        </p>
      ) : isPageItem(item) && !pages.find((p) => p.slug === item.page)?.published ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Draft page — the link appears on your site once the page is published.
        </p>
      ) : null}
    </div>
  );
}

function MenuCard({ menu, pages, saving, error, onItemsChange, onSave, dirty }: {
  menu: Menu;
  pages: PageOption[];
  saving: boolean;
  error: string;
  onItemsChange: (items: MenuItem[]) => void;
  onSave: () => void;
  dirty: boolean;
}) {
  const problem = menuProblem(menu.items);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{menu.name} menu</CardTitle>
        <CardDescription>{MENU_HINTS[menu.slug] ?? "Navigation links on your site."}</CardDescription>
      </CardHeader>
      <CardContent>
        {menu.items.length === 0 && (
          <p className="py-4 text-sm text-muted-foreground">
            No links yet — this menu is hidden on your site until you add some.
          </p>
        )}
        {menu.items.map((item, index) => (
          <MenuItemRow
            key={index}
            item={item}
            index={index}
            count={menu.items.length}
            pages={pages}
            onChange={(next) => {
              const items = menu.items.slice();
              items[index] = next;
              onItemsChange(items);
            }}
            onMove={(delta) => onItemsChange(moveItem(menu.items, index, delta))}
            onRemove={() => onItemsChange(menu.items.filter((_, i) => i !== index))}
          />
        ))}
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => onItemsChange([...menu.items, { label: "", url: "" }])}
        >
          <Plus className="h-3.5 w-3.5" />
          Add link
        </Button>
      </CardContent>
      <CardFooter className="justify-between gap-3">
        <p className="text-sm text-destructive">{error}</p>
        <Button onClick={onSave} disabled={!dirty || !!problem || saving} size="sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save {menu.name.toLowerCase()} menu
        </Button>
      </CardFooter>
    </Card>
  );
}

export function MenusPage() {
  const { api, refreshStatus } = useOutletContext<ClientOutletContext>();
  const [menus, setMenus] = useState<Menu[] | null>(null);
  const [savedJson, setSavedJson] = useState<Record<string, string>>({});
  const [pages, setPages] = useState<PageOption[]>([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!api) return;
    Promise.all([
      api.request<{ menus?: Menu[] }>("/tenant/menus"),
      api.request<{ pages?: PageOption[] }>("/tenant/pages"),
    ])
      .then(([menusData, pagesData]) => {
        const loaded = menusData.menus ?? [];
        setMenus(loaded);
        setSavedJson(Object.fromEntries(loaded.map((m) => [m.slug, JSON.stringify(m.items)])));
        setPages(pagesData.pages ?? []);
      })
      .catch((err: Error) => setError(err.message));
  }, [api]);

  const setMenuItems = (slug: string, items: MenuItem[]) => {
    setMenus((prev) => (prev ? prev.map((menu) => (menu.slug === slug ? { ...menu, items } : menu)) : prev));
  };

  const saveMenu = async (menu: Menu) => {
    if (!api) return;
    setSaving(menu.slug);
    setSaveErrors((prev) => ({ ...prev, [menu.slug]: "" }));
    try {
      const result = await api.request<{ items: MenuItem[] }>(`/tenant/menus/${menu.slug}`, {
        method: "PUT",
        body: JSON.stringify({ items: toSavePayload(menu.items) }),
      });
      setMenuItems(menu.slug, result.items);
      setSavedJson((prev) => ({ ...prev, [menu.slug]: JSON.stringify(result.items) }));
      refreshStatus?.();
    } catch (err) {
      setSaveErrors((prev) => ({ ...prev, [menu.slug]: err instanceof Error ? err.message : String(err) }));
    } finally {
      setSaving("");
    }
  };

  return (
    <div>
      <PageHeader
        title="Navigation"
        description="Manage the navigation menus on your site. Links to draft pages stay hidden until the page is published."
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!menus && !error && <LoadingSpinner className="py-20" />}

      {menus && (
        <div className="space-y-6">
          {menus.map((menu) => (
            <MenuCard
              key={menu.slug}
              menu={menu}
              pages={pages}
              saving={saving === menu.slug}
              error={saveErrors[menu.slug] ?? ""}
              dirty={JSON.stringify(menu.items) !== savedJson[menu.slug]}
              onItemsChange={(items) => setMenuItems(menu.slug, items)}
              onSave={() => saveMenu(menu)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
